/**
 * usePurchaseInvoiceForm Hook
 * Alış faturası form yönetimi - state ve handlers
 */

import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { SelectChangeEvent } from '@mui/material';
import { Account, InvoiceItem, Invoice, TevkifatType } from '../components/types/invoice.types';
import { recalculateLineItem, formatQuantityForDisplay, parseQuantityString, tevkifatTypes } from '../utils/invoiceCalculations';
import { setItemDisplay, commitItemNumber, clearDiscountDisplays, type EditableNumberField } from '../utils/invoiceItemFields';
import { lotFlagsFromItem } from '../utils/invoiceLot';
import type { InventoryItem } from '../../inventory/types/inventory.types';
import { useAuth, type Warehouse } from '../../../../contexts/AuthContext';
import { UNIT_OPTIONS } from '../../../../constants/units';

// Birim seçenekleri
export const unitOptions = [...UNIT_OPTIONS];

// Bugünün tarihini local timezone'da al
export const getTodayLocal = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

interface UsePurchaseInvoiceFormProps {
  companyId: string | null;
  inventoryItems: InventoryItem[];
  /** Verilirse AuthContext depoları yerine kullanılır (ör. manager için yalnız şube depoları). */
  warehouses?: Warehouse[];
  /** Route dışı açılışta (manager paneli) düzenlenecek fatura. */
  initialEditingInvoiceId?: string | null;
}

export const usePurchaseInvoiceForm = ({
  companyId,
  inventoryItems,
  warehouses: warehousesOverride,
  initialEditingInvoiceId,
}: UsePurchaseInvoiceFormProps) => {
  const navigate = useNavigate();
  const { warehouses: authWarehouses } = useAuth();
  const warehouses = warehousesOverride ?? authWarehouses;
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  
  // URL parametreleri
  const { invoiceId: invoiceIdFromParams } = useParams<{ invoiceId?: string }>();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isFromOffer = searchParams.get('fromOffer') === 'true';
  const ettnFromUrl = searchParams.get('ettn');
  
  // Düzenleme modu
  const [editingInvoiceId, setEditingInvoiceId] = useState<string | null>(initialEditingInvoiceId || invoiceIdFromParams || null);
  const pageTitle = editingInvoiceId
    ? tt("Alış Faturası Düzenle", "Edit Purchase Invoice")
    : tt("Yeni Alış Faturası", "New Purchase Invoice");
  
  // Cari hesap state
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  
  // Fatura bilgileri state
  const [invoiceDate, setInvoiceDate] = useState<string>(getTodayLocal());
  const [dueDate, setDueDate] = useState<string>('');
  const [invoiceNumber, setInvoiceNumber] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  // PYS: belgenin bağlı olduğu proje (opsiyonel; null → projeye bağlı değil)
  const [projectId, setProjectId] = useState<number | null>(null);
  const [originalInvoiceData, setOriginalInvoiceData] = useState<Invoice | null>(null);
  
  // Ürün state
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<InvoiceItem[]>([]);
  // İndirim kolonu giriş modu (tüm kalemler için ortak): yüzde mi tutar mı
  const [discountMode, setDiscountMode] = useState<'PERCENT' | 'AMOUNT'>('PERCENT');
  
  // Para birimi state
  const [currency, setCurrency] = useState<string>('TRY');
  const [exchangeRate, setExchangeRate] = useState<string>('1');
  const [availableCurrencies, setAvailableCurrencies] = useState<{code: string, active: boolean}[]>([
    { code: 'TRY', active: true }
  ]);
  
  // Tevkifat state
  const [showTevkifat, setShowTevkifat] = useState<boolean>(false);
  const [tevkifatRate, setTevkifatRate] = useState<string>("0");
  const [tevkifatAmount, setTevkifatAmount] = useState<number>(0);
  const [tevkifatType, setTevkifatType] = useState<string>("");
  
  // Stopaj state
  const [showStopaj, setShowStopaj] = useState<boolean>(false);
  const [stopajRate, setStopajRate] = useState<number>(0);
  const [stopajAmount, setStopajAmount] = useState<number>(0);
  
  // Ek menü state
  const [showAdditionalMenu, setShowAdditionalMenu] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  
  // Orijinal fatura tutarı (E-fatura için)
  const [originalInvoiceTotal, setOriginalInvoiceTotal] = useState<string>('');
  const [invoiceDifference, setInvoiceDifference] = useState<number>(0);
  
  // Toplu depo seçimi
  const [isBulkWarehouseModalOpen, setIsBulkWarehouseModalOpen] = useState<boolean>(false);
  const [bulkWarehouseId, setBulkWarehouseId] = useState<string>('');
  
  // Depo seçimi
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string | null>(null);
  
  // ===== HANDLERS =====
  
  // Geri butonu
  const handleGoBack = useCallback(() => {
    navigate('/fatura/alis');
  }, [navigate]);
  
  // Cari modal handlers
  const handleOpenAccountModal = useCallback(() => {
    setIsAccountModalOpen(true);
  }, []);
  
  const handleCloseAccountModal = useCallback(() => {
    setIsAccountModalOpen(false);
  }, []);
  
  const handleSelectAccount = useCallback((account: Account) => {
    setSelectedAccount(account);
    setIsAccountModalOpen(false);
  }, []);
  
  // Ürün modal handlers
  const handleOpenItemModal = useCallback(() => {
    setIsItemModalOpen(true);
  }, []);
  
  const handleCloseItemModal = useCallback(() => {
    setIsItemModalOpen(false);
  }, []);
  
  // Ürün ekleme (stoktan)
  const handleAddItem = useCallback((item: InventoryItem, targetWarehouseId?: string) => {
    let unitPriceForInvoice = item.purchasePrice || 0; // Alış faturası için purchasePrice kullan
    const vatRate = item.vatRate || 0;
    
    // enteredPrice ve priceType belirle
    let enteredPrice = unitPriceForInvoice;
    let priceType: 'VAT_EXCLUDED' | 'VAT_INCLUDED' = 'VAT_EXCLUDED';

    if (item.vatStatus === 'included' && vatRate > 0) {
      enteredPrice = unitPriceForInvoice;
      priceType = 'VAT_INCLUDED';
    }

    const warehouseIdToUse = targetWarehouseId || selectedWarehouseId || item.warehouseStocks?.[0]?.warehouseId || undefined;

    // Her "Ekle" yeni satır açar: aynı ürün farklı fiyatla ayrı satırda olabilir. Ürünün alış
    // fiyatı faturadaki en pahalı satırdan güncellenir (sunucu: PurchasePriceCalculator).
    // (Barkod okutmada aynı satırın miktarı artar: addScannedLine.)
    const initialQuantity = 1;

    {
      const newItemToAdd: InvoiceItem = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        itemId: item.id,
        itemName: item.name,
        quantity: initialQuantity,
        displayQuantity: formatQuantityForDisplay(initialQuantity),
        unit: item.unit || 'adet',
        enteredPrice: enteredPrice,
        priceType: priceType,
        unitPrice: 0,
        unitPriceWithVat: 0,
        vatRate: vatRate,
        sctRate: item.sctRate || 0,
        discountRate: 0,
        discountAmount: 0,
        warehouseId: warehouseIdToUse,
        lineTotal: 0, 
        netTotal: 0, 
        vatAmount: 0, 
        sctAmount: 0, 
        grossTotal: 0
      };
      const calculatedItem = recalculateLineItem({ ...newItemToAdd, ...lotFlagsFromItem(item) });
      setSelectedItems(prev => [...prev, calculatedItem]);
    }
    setIsItemModalOpen(false);
  }, [selectedWarehouseId]);
  
  // Manuel ürün ekleme (stoksuz)
  const handleAddManualItem = useCallback(() => {
    const initialQuantity = 1;
    const defaultWhId = warehouses.find(w => w.isDefault)?.id || (warehouses.length > 0 ? warehouses[0].id : undefined);
    
    const newItem: InvoiceItem = {
      id: 'manual_' + Date.now().toString(),
      itemId: 'manual_item',
      itemName: '',
      quantity: initialQuantity,
      displayQuantity: formatQuantityForDisplay(initialQuantity),
      unit: 'adet',
      enteredPrice: 0,
      priceType: 'VAT_EXCLUDED',
      unitPrice: 0,
      unitPriceWithVat: 0,
      vatRate: 20,
      sctRate: 0,
      discountRate: 0,
      discountAmount: 0,
      warehouseId: defaultWhId,
      lineTotal: 0, 
      netTotal: 0, 
      vatAmount: 0, 
      sctAmount: 0, 
      grossTotal: 0
    };
    const calculatedItem = recalculateLineItem(newItem);
    setSelectedItems(prev => [...prev, calculatedItem]);
  }, [warehouses]);
  
  // Ürün değişiklik handlers
  const handleQuantityChange = useCallback((id: string, value: number) => {
    setSelectedItems(prevItems => 
      prevItems.map(item => {
        if (item.id === id) {
          const updatedItem = { ...item, quantity: value };
          return recalculateLineItem(updatedItem);
        }
        return item;
      })
    );
  }, []);
  
  const handleDisplayQuantityChange = useCallback((id: string, newDisplayValue: string) => {
    const sanitizedValue = newDisplayValue.replace(/\./g, ',');
    if ((sanitizedValue.match(/,/g) || []).length > 1) return;
    if (sanitizedValue !== '' && !/^[0-9]*[,]?[0-9]*$/.test(sanitizedValue)) return;
  
    setSelectedItems(prevItems =>
      prevItems.map(item =>
        item.id === id ? { ...item, displayQuantity: sanitizedValue } : item
      )
    );
  }, []);
  
  const handleQuantityCommitOnBlur = useCallback((id: string) => {
    setSelectedItems(prevItems =>
      prevItems.map(item => {
        if (item.id !== id) return item;
        const raw = (item.displayQuantity ?? '').trim();
        const newQuantity = parseQuantityString(raw);
        // Alan boş bırakıldıysa metin boş kalır (otomatik "0" yazılmaz), hesaplamada 0
        // sayılır. Miktar satır tutarını değiştirdiği için indirim metinleri modelden
        // yeniden üretilir.
        return recalculateLineItem(clearDiscountDisplays({
          ...item,
          quantity: newQuantity,
          displayQuantity: raw === '' ? '' : formatQuantityForDisplay(newQuantity),
        }));
      })
    );
  }, []);
  
  const handleEnteredPriceChange = useCallback((id: string, value: number) => {
    setSelectedItems(prevItems => 
      prevItems.map(item => {
        if (item.id === id) {
          const updatedItem = { ...item, enteredPrice: value };
          return recalculateLineItem(updatedItem);
        }
        return item;
      })
    );
  }, []);
  
  const handlePriceTypeChange = useCallback((id: string, value: 'VAT_EXCLUDED' | 'VAT_INCLUDED') => {
    setSelectedItems(prevItems => 
      prevItems.map(item => {
        if (item.id === id) {
          const updatedItem = { ...item, priceType: value };
          return recalculateLineItem(updatedItem);
        }
        return item;
      })
    );
  }, []);
  
  const handleVatRateChange = useCallback((id: string, value: number) => {
    // 0-100 araligina clamp: kullanici 120 gibi bir deger girse de UI
    // hemen 100'e sabitlenir ve backend [Range(0,100)] validation'ina takilmaz.
    const safeValue = Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : 0;
    setSelectedItems(prevItems => 
      prevItems.map(item => {
        if (item.id === id) {
          const updatedItem = { ...item, vatRate: safeValue };
          return recalculateLineItem(updatedItem);
        }
        return item;
      })
    );
  }, []);
  
  const handleDiscountRateChange = useCallback((id: string, value: number) => {
    const safeValue = Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : 0;
    setSelectedItems(prevItems => 
      prevItems.map(item => {
        if (item.id === id) {
          const updatedItem = { ...item, discountType: 'PERCENT' as const, discountRate: safeValue };
          return recalculateLineItem(updatedItem);
        }
        return item;
      })
    );
  }, []);

  // AMOUNT modunda satır indirim tutarı değişimi (negatif değer engellenir)
  const handleDiscountAmountChange = useCallback((id: string, value: number) => {
    const safeValue = Number.isFinite(value) ? Math.max(0, value) : 0;
    setSelectedItems(prevItems => 
      prevItems.map(item => {
        if (item.id === id) {
          const updatedItem = { ...item, discountType: 'AMOUNT' as const, discountAmount: safeValue };
          return recalculateLineItem(updatedItem);
        }
        return item;
      })
    );
  }, []);

  // Sayısal alanlar: kullanıcı yazarken yalnızca metin güncellenir, sayıya çevirme
  // odak çıkışında/Enter'da yapılır. Böylece alan boş bırakılabilir ve otomatik
  // sıfır yazılmaz. Oran sıkıştırma (0-100) ve negatif engeli commitItemNumber içinde.
  const handleItemDisplayChange = useCallback((id: string, field: EditableNumberField, raw: string) => {
    setSelectedItems(prevItems => prevItems.map(item => (item.id === id ? setItemDisplay(item, field, raw) : item)));
  }, []);

  const handleItemNumberCommit = useCallback((id: string, field: EditableNumberField) => {
    setSelectedItems(prevItems => prevItems.map(item => (item.id === id ? commitItemNumber(item, field) : item)));
  }, []);

  // Mod değişimi → tüm kalemlere uygulanır ve yeniden hesaplanır. İki indirim alanının
  // metinleri birbirinden türediği için sıfırlanır.
  const handleDiscountModeChange = useCallback((mode: 'PERCENT' | 'AMOUNT') => {
    setDiscountMode(mode);
    setSelectedItems(prevItems =>
      prevItems.map(item => recalculateLineItem(clearDiscountDisplays({ ...item, discountType: mode })))
    );
  }, []);
  
  const handleRemoveItem = useCallback((id: string) => {
    setSelectedItems(prevItems => prevItems.filter(item => item.id !== id));
  }, []);
  
  const handleItemNameChange = useCallback((id: string, value: string) => {
    setSelectedItems(prevItems => 
      prevItems.map(item => item.id === id ? { ...item, itemName: value } : item)
    );
  }, []);
  
  const handleUnitChange = useCallback((id: string, value: string) => {
    setSelectedItems(prevItems => 
      prevItems.map(item => item.id === id ? { ...item, unit: value } : item)
    );
  }, []);
  
  const handleWarehouseChangeForItem = useCallback((id: string, warehouseId: string) => {
    setSelectedItems(prevItems => 
      prevItems.map(item => item.id === id ? { ...item, warehouseId } : item)
    );
  }, []);
  
  // Tevkifat handlers
  const handleTevkifatTypeChange = useCallback((event: SelectChangeEvent<string>) => {
    const selectedCode = event.target.value;
    setTevkifatType(selectedCode);
    
    if (selectedCode) {
      const selectedType = tevkifatTypes.find(type => type.code === selectedCode);
      if (selectedType) {
        setTevkifatRate(selectedType.rate);
      }
    }
  }, []);
  
  const handleTevkifatRateChange = useCallback((event: SelectChangeEvent) => {
    setTevkifatRate(event.target.value);
  }, []);
  
  const handleAddTevkifat = useCallback(() => {
    setShowTevkifat(true);
    setAnchorEl(null);
    setShowAdditionalMenu(false);
  }, []);
  
  const handleRemoveTevkifat = useCallback(() => {
    setShowTevkifat(false);
    setTevkifatRate("0");
    setTevkifatAmount(0);
    setTevkifatType("");
  }, []);
  
  // Stopaj handlers
  const handleStopajRateChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newRate = parseFloat(event.target.value);
    setStopajRate(isNaN(newRate) ? 0 : newRate);
  }, []);
  
  const handleAddStopaj = useCallback(() => {
    setShowStopaj(true);
    setAnchorEl(null);
    setShowAdditionalMenu(false);
  }, []);
  
  const handleRemoveStopaj = useCallback(() => {
    setShowStopaj(false);
    setStopajRate(0);
    setStopajAmount(0);
  }, []);
  
  // Ek menü handlers
  const handleToggleAdditionalMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setShowAdditionalMenu(true);
  }, []);
  
  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null);
    setShowAdditionalMenu(false);
  }, []);
  
  // Para birimi handlers
  const handleCurrencyChange = useCallback((event: SelectChangeEvent) => {
    const newCurrency = event.target.value;
    setCurrency(newCurrency);
    if (newCurrency === 'TRY') {
      setExchangeRate('1');
    }
  }, []);
  
  // Toplu depo seçimi
  const handleBulkWarehouseSelect = useCallback(() => {
    if (!bulkWarehouseId) {
      alert('Lütfen bir depo seçin');
      return;
    }
    
    setSelectedItems(prevItems => 
      prevItems.map(item => ({ ...item, warehouseId: bulkWarehouseId }))
    );
    setIsBulkWarehouseModalOpen(false);
    setBulkWarehouseId('');
  }, [bulkWarehouseId]);
  
  // Varsayılan depo seçimi
  useEffect(() => {
    if (warehouses.length > 0 && !selectedWarehouseId) {
      const defaultWarehouse = warehouses.find(wh => wh.isDefault);
      if (defaultWarehouse) {
        setSelectedWarehouseId(defaultWarehouse.id);
      } else {
        setSelectedWarehouseId(warehouses[0].id);
      }
    }
  }, [warehouses, selectedWarehouseId]);
  
  // Depoya göre ürünleri filtreleme (ürün seçim modalı için)
  const getWarehousesForItem = useCallback((itemId: string) => {
    if (itemId.startsWith('manual_')) {
      return [];
    }
    
    const filteredWarehouses = warehouses.filter(warehouse => {
      const itemInWarehouse = inventoryItems.some(item => 
        item.id === itemId && item.warehouseStocks?.some(ws => ws.warehouseId === warehouse.id)
      );
      return itemInWarehouse;
    });
    
    return filteredWarehouses.length > 0 ? filteredWarehouses : warehouses;
  }, [warehouses, inventoryItems]);
  
  // Para formatı helper
  const formatCurrency = useCallback((amount: number): string => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: currency }).format(amount);
  }, [currency]);
  
  const getCurrencySymbol = useCallback((code: string): string => {
    switch(code) {
      case 'TRY': return '₺';
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'JPY': return '¥';
      default: return code;
    }
  }, []);
  
  return {
    // URL params
    editingInvoiceId,
    setEditingInvoiceId,
    isFromOffer,
    ettnFromUrl,
    pageTitle,
    
    // Cari hesap
    selectedAccount,
    setSelectedAccount,
    isAccountModalOpen,
    setIsAccountModalOpen,
    handleOpenAccountModal,
    handleCloseAccountModal,
    handleSelectAccount,
    
    // Fatura bilgileri
    invoiceDate,
    setInvoiceDate,
    dueDate,
    setDueDate,
    invoiceNumber,
    setInvoiceNumber,
    notes,
    setNotes,
    projectId,
    setProjectId,
    originalInvoiceData,
    setOriginalInvoiceData,
    
    // Ürünler
    isItemModalOpen,
    setIsItemModalOpen,
    selectedItems,
    setSelectedItems,
    handleOpenItemModal,
    handleCloseItemModal,
    handleAddItem,
    handleAddManualItem,
    handleQuantityChange,
    handleDisplayQuantityChange,
    handleQuantityCommitOnBlur,
    handleEnteredPriceChange,
    handlePriceTypeChange,
    handleVatRateChange,
    handleDiscountRateChange,
    handleDiscountAmountChange,
    handleItemDisplayChange,
    handleItemNumberCommit,
    discountMode,
    setDiscountMode,
    handleDiscountModeChange,
    handleRemoveItem,
    handleItemNameChange,
    handleUnitChange,
    handleWarehouseChangeForItem,
    getWarehousesForItem,
    
    // Para birimi
    currency,
    setCurrency,
    exchangeRate,
    setExchangeRate,
    availableCurrencies,
    setAvailableCurrencies,
    handleCurrencyChange,
    formatCurrency,
    getCurrencySymbol,
    
    // Tevkifat
    showTevkifat,
    setShowTevkifat,
    tevkifatRate,
    setTevkifatRate,
    tevkifatAmount,
    setTevkifatAmount,
    tevkifatType,
    setTevkifatType,
    handleTevkifatTypeChange,
    handleTevkifatRateChange,
    handleAddTevkifat,
    handleRemoveTevkifat,
    
    // Stopaj
    showStopaj,
    setShowStopaj,
    stopajRate,
    setStopajRate,
    stopajAmount,
    setStopajAmount,
    handleStopajRateChange,
    handleAddStopaj,
    handleRemoveStopaj,
    
    // Ek menü
    showAdditionalMenu,
    anchorEl,
    handleToggleAdditionalMenu,
    handleCloseMenu,
    
    // Orijinal fatura tutarı
    originalInvoiceTotal,
    setOriginalInvoiceTotal,
    invoiceDifference,
    setInvoiceDifference,
    
    // Toplu depo
    isBulkWarehouseModalOpen,
    setIsBulkWarehouseModalOpen,
    bulkWarehouseId,
    setBulkWarehouseId,
    handleBulkWarehouseSelect,
    
    // Depo
    selectedWarehouseId,
    setSelectedWarehouseId,
    
    // Navigation
    handleGoBack,
    navigate,
    
    // Sabitler
    unitOptions,
  };
};
